// Libs
import { memoize } from 'lodash';
import { createSelector } from 'reselect';

// App
import { AppState } from 'state/types';
import { ShortIdCompress } from 'common/short-id-compress';
import { Entity, getEntityShortId } from '@neptune/shared/entity-domain';
import { getLeaderboardState } from '@neptune/shared/leaderboard-business-logic';
import { getCurrentRouteParams } from '@neptune/shared/routing-business-logic';
import { getCurrentProjectProjectKey } from '@neptune/current-project-business-logic';

import getParentState from '../selectors';
import { CompareState } from './types';

export function getCompareState(state: AppState): CompareState {
  return getParentState(state).compare;
}

const getAutoModeCompareIdsForCurrentProject = (state: AppState): string[] | undefined =>
  getCompareAutoModeForCurrentProject(state) ? getAutoModeCompareIds(state) : undefined;

const getCompareCurrentRouteParams = (state: AppState) => getCurrentRouteParams(state).compare;

const extractCached = memoize(ShortIdCompress.parse, (...args) => args.join('#'));

export const getCompareIdsForCurrentProject: (state: AppState) => string[] = createSelector(
  getCompareAutoModeForCurrentProject,
  getCompareCurrentRouteParams,
  getCurrentProjectProjectKey,
  getAutoModeCompareIdsForCurrentProject,
  (
    autoMode: boolean,
    packedCompareIds: string | undefined,
    projectKey: string | undefined,
    autoModeIds: string[] | undefined,
  ) => {
    if (autoMode) {
      return autoModeIds || [];
    }

    if (projectKey != null && packedCompareIds != null) {
      return extractCached(packedCompareIds, projectKey);
    }

    return [];
  },
);

export function getCompareAutoModeForCurrentProject(state: AppState): boolean {
  return getCompareCurrentRouteParams(state) === 'auto';
}

const ID_SEPARATOR = '|';

// We need to keep the array stable as long as the ids aren't actually changing,
// so we use string as an intermediate value that keeps === equality as we need.
const getAutoModeCompareIdsAsString = createSelector(
  [(state) => getLeaderboardState<Entity>(state, 'runs')],
  (leaderboard): string =>
    leaderboard.entries
      .slice(0, 5)
      .map((entry: Entity) => getEntityShortId(entry))
      .sort()
      .join(ID_SEPARATOR),
);

export const getAutoModeCompareIds = createSelector(
  getAutoModeCompareIdsAsString,
  (stringValue): string[] => stringValue.split(ID_SEPARATOR),
);

export default getCompareState;
